/**
 * useEligibleDevices Hook
 * Phase 1 Day 45 - Extract eligible devices calculations
 *
 * Calculates which devices are eligible for lockdown/release based on their lockdownState.
 * Provides both counts and full device arrays for UI display.
 */

import { useMemo } from "react";
import logger from "../utils/logger";

export function useEligibleDevices(devices, deviceStates) {
  // Count of devices eligible for lockdown (not currently locked down)
  const eligibleForLockdown = useMemo(() => {
    const count =
      devices?.filter(
        (device) =>
          deviceStates[device.deviceId]?.lockdownState !== "Lockdown On"
      ).length || 0;
    logger.log(
      `[eligibleForLockdown] 📊 Calculated ${count} devices eligible for lockdown`
    );
    return count;
  }, [devices, deviceStates]);

  // Count of devices eligible for release (currently locked down)
  const eligibleForRelease = useMemo(() => {
    const count =
      devices?.filter(
        (device) =>
          deviceStates[device.deviceId]?.lockdownState === "Lockdown On"
      ).length || 0;
    logger.log(
      `[eligibleForRelease] 📊 Calculated ${count} devices eligible for release`
    );
    return count;
  }, [devices, deviceStates]);

  // Full device objects eligible for lockdown (with deduplication)
  const eligibleDevicesForLockdown = useMemo(() => {
    const eligibleDevices =
      devices?.filter(
        (device) =>
          deviceStates[device.deviceId]?.lockdownState !== "Lockdown On"
      ) || [];

    // Deduplicate by deviceId and enrich with model for compatibility checks
    const uniqueDevices = Array.from(
      new Map(
        eligibleDevices.map((device) => [
          device.deviceId,
          { ...device, model: deviceStates[device.deviceId]?.model },
        ])
      ).values()
    );

    if (eligibleDevices.length !== uniqueDevices.length) {
      logger.warn(
        `[eligibleDevicesForLockdown] ⚠️ Found ${
          eligibleDevices.length - uniqueDevices.length
        } duplicate device(s), deduplicated to ${uniqueDevices.length}`
      );
    }

    logger.log(
      `[eligibleDevicesForLockdown] 📋 Found ${uniqueDevices.length} unique devices eligible for lockdown`
    );
    return uniqueDevices;
  }, [devices, deviceStates]);

  // Full device objects eligible for release (with deduplication)
  const eligibleDevicesForRelease = useMemo(() => {
    const eligibleDevices =
      devices?.filter(
        (device) =>
          deviceStates[device.deviceId]?.lockdownState === "Lockdown On"
      ) || [];

    // Deduplicate by deviceId and enrich with model for compatibility checks
    const uniqueDevices = Array.from(
      new Map(
        eligibleDevices.map((device) => [
          device.deviceId,
          { ...device, model: deviceStates[device.deviceId]?.model },
        ])
      ).values()
    );

    if (eligibleDevices.length !== uniqueDevices.length) {
      logger.warn(
        `[eligibleDevicesForRelease] ⚠️ Found ${
          eligibleDevices.length - uniqueDevices.length
        } duplicate device(s), deduplicated to ${uniqueDevices.length}`
      );
    }

    logger.log(
      `[eligibleDevicesForRelease] 📋 Found ${uniqueDevices.length} unique devices eligible for release`
    );
    return uniqueDevices;
  }, [devices, deviceStates]);

  // Check if all devices have their state loaded
  const allDeviceStatesLoaded = useMemo(() => {
    if (!devices || devices.length === 0) return false;
    const loaded = devices.every((device) => deviceStates[device.deviceId]);
    const unloadedDevices = devices
      .filter((d) => !deviceStates[d.deviceId])
      .map((d) => d.deviceId);
    logger.log(
      `[allDeviceStatesLoaded] ${loaded ? "✅" : "⏳"} ${
        Object.keys(deviceStates).length
      }/${devices.length} device states loaded`
    );
    if (!loaded && unloadedDevices.length > 0) {
      logger.log(
        `[allDeviceStatesLoaded] Unloaded devices: ${unloadedDevices.join(
          ", "
        )}`
      );
    }
    return loaded;
  }, [devices, deviceStates]);

  return {
    eligibleForLockdown,
    eligibleForRelease,
    eligibleDevicesForLockdown,
    eligibleDevicesForRelease,
    allDeviceStatesLoaded,
  };
}
